import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { CheckCircle2, DollarSign, LayoutDashboard, Terminal } from "lucide-react";

export const Route = createFileRoute("/revenda-iptv/pos-pago")({
  head: () => ({
    meta: [
      { title: "Revenda IPTV Pós-Pago Profissional - Estabilidade e ROI | P2 PRO" },
      { name: "description", content: "A melhor revenda IPTV pós-pago do Brasil. Controle total do fluxo de caixa, créditos sob demanda e suporte VIP. Comece hoje sem estoque parado." },
      { name: "keywords", content: "revenda iptv pos-pago, painel iptv pos-pago oficial, como revender iptv pos-pago, melhor servidor iptv pos-pago" },
    ],
  }),
  component: () => (
    <SEOPage
      variant="split"
      hideStats={false}
      showTestimonials={true}
      category="Modalidades de Negócio"
      title="REVENDA IPTV PÓS-PAGO: A REVOLUÇÃO DO FLUXO DE CAIXA"
      subtitle="O modelo preferido dos grandes distribuidores. Domine o mercado pagando apenas pelo que vender."
      introParagraphs={[
        "No competitivo ecossistema de streaming, a agilidade financeira é o que separa os amadores dos profissionais. A Revenda IPTV Pós-Pago da P2 PRO foi estruturada para oferecer exatamente essa vantagem competitiva. Enquanto a maioria dos revendedores fica 'presa' a créditos comprados antecipadamente que podem expirar ou demorar a ser vendidos, nossos parceiros pós-pago operam com capital de giro livre, reinvestindo seus lucros de forma estratégica desde o primeiro dia.",
        "Neste modelo, o painel master da P2 PRO torna-se seu aliado tecnológico de elite. Você tem autonomia total para gerenciar usuários, criar sub-revendedores e configurar planos personalizados com sinal 4K nativo. O custo de cada ativação é debitado de forma automatizada e transparente apenas no momento em que você confirma a venda para seu cliente final. É a máxima eficiência operacional aliada ao menor risco financeiro possível.",
        "Se você já possui uma carteira de clientes e busca escala, ou se está começando e quer o máximo de segurança para seu investimento inicial baixo, a revenda pós-paga é o caminho definitivo. Combinamos os servidores mais potentes do Brasil (P2PRO, OneX e Prime Play) em uma única interface inteligente para que sua operação nunca pare de crescer."
      ]}
      sections={[
        {
          title: "Inovação Operacional Pós-Paga",
          type: "grid",
          content: (
            <>
              {[
                { title: "Infraestrutura na Nuvem", desc: "Hospedagem de alto desempenho que garante que seu painel master esteja sempre online e rápido.", icon: <Terminal className="text-electric" /> },
                { title: "Dashboard Master", desc: "Controle granular de ativações, renovações e sub-revendas em uma única tela profissional.", icon: <LayoutDashboard className="text-purple-400" /> },
                { title: "Faturamento Dinâmico", desc: "Sistema de cobrança sob medida para o seu volume de vendas, otimizando suas taxas bancárias.", icon: <DollarSign className="text-green-400" /> }
              ].map((item, i) => (
                <div key={i} className="p-10 rounded-[2.5rem] bg-white/[0.02] border border-white/5 hover:border-electric/30 transition-all text-center">
                  <div className="flex justify-center mb-6 text-4xl">{item.icon}</div>
                  <h3 className="text-xl font-black mb-4 italic uppercase">{item.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </>
          )
        },
        {
          title: "Por que migrar para o Pós-Pago P2 PRO?",
          type: "featured",
          content: (
            <div className="grid lg:grid-cols-2 gap-12 items-center text-white">
              <div>
                <p className="text-lg leading-relaxed mb-8 italic uppercase font-black italic">O fim da barreira de estoque</p>
                <div className="space-y-6">
                  {[
                    "Liberação de acessos Full HD e 4K instantânea",
                    "Monitoramento de latência e qualidade de sinal",
                    "Proteção contra ataques DDoS em nível de rede",
                    "Integração com os principais apps (OneX, P2Play)"
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-4">
                      <div className="w-6 h-6 rounded-full bg-electric/20 flex items-center justify-center border border-electric/30">
                        <CheckCircle2 className="w-3 h-3 text-electric" />
                      </div>
                      <span className="font-bold text-sm text-white/90">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-black/40 p-10 rounded-[3rem] border border-white/10 backdrop-blur-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-electric/10 blur-3xl rounded-full" />
                <h4 className="text-2xl font-black mb-4 text-electric italic">ROI OTIMIZADO</h4>
                <p className="text-white/60 text-sm leading-relaxed">
                  Reduzimos seu custo operacional fixo em até 60% eliminando a necessidade de 'estoque parado'. Cada real investido no pós-pago retorna como lucro direto na sua conta.
                </p>
              </div>
            </div>
          )
        }
      ]}
      benefits={[
        { title: "Capital de Giro Livre", description: "Não compre créditos antecipadamente. Pague apenas pelo que consumir." },
        { title: "Estabilidade de Elite", description: "Utilize o sinal do P2PRO, referência nacional em transmissão sem travas." },
        { title: "Autonomia Técnica", description: "Configurações de DNS e links M3U liberadas para personalização master." }
      ]}
      howItWorks={[
        { title: "Ativação de Painel", description: "Configuramos sua estrutura master em poucos minutos." },
        { title: "Venda de Acesso", description: "O cliente paga você e escolhe o plano de canais." },
        { title: "Consumo de Crédito", description: "O sistema debita a ativação conforme a necessidade." },
        { title: "Lucro Real", description: "Sua margem é protegida pelo baixo custo unitário operacional." }
      ]}
      diferenciais={[
        { title: "EPG Atualizado", description: "Guia de programação completo e automático para todos os canais." },
        { title: "Canais 24h VIP", description: "Conteúdo focado em desenhos, séries e novelas sem interrupções." },
        { title: "Backup Cloud", description: "Sua base de dados de clientes replicada em múltiplos servidores." }
      ]}
      faq={[
        { question: "Como funciona o acerto financeiro?", answer: "Realizamos fechamentos periódicos ou baseados em volume, sempre facilitados via Pix ou Cartão." },
        { question: "O painel master tem limite de clientes?", answer: "Não. Nossa arquitetura suporta desde pequenos revendedores até grandes operadoras regionais." },
        { question: "Posso oferecer testes grátis?", answer: "Sim, os testes de 6 horas são ilimitados e gratuitos para você atrair novos assinantes." }
      ]}
      breadcrumbs={[
        { label: "Revenda IPTV", href: "/revenda-iptv" },
        { label: "Pós-Pago", href: "/revenda-iptv/pos-pago" }
      ]}
      relatedArticles={[
        { title: "Como Ser um Revendedor", href: "/revenda-iptv/como-ser" },
        { title: "Quanto Ganha um Revendedor", href: "/revenda-iptv/quanto-ganha" },
        { title: "Revenda para Iniciantes", href: "/revenda-iptv/iniciantes" }
      ]}
    />
  ),
});
